/**
 * Canonical gas constants for all Play Project on-chain operations.
 *
 * Single source of truth — all packages (sdk, deployer, indexer, contracts
 * scripts) must import from here rather than defining local copies.
 *
 * Contract-side expectations (nft_collection.tact / nft_item.tact):
 *   - PublicMint overhead:  ton("0.1")  — MINT_GAS_BASE + per-NFT covers this
 *   - NFT deploy value:     ton("0.05") — forwarded per item in batch
 *   - NFT transfer min_gas: ton("0.01") — hardcoded in nft_item.tact
 *   - Withdraw min balance: ton("1")    — collection must retain at least 1 TON
 *
 * ⚠️  If you change a constant here, you MUST audit the contract source
 *     (packages/contracts/contracts/*.tact) for the corresponding hardcoded
 *     value and update it in lock-step, because Tact does not import TS.
 *     See #340 (P0) for the rationale on why this file exists.
 */
import { toNano } from '@ton/core';
// ── User-facing operations (SDK / frontends) ───────────────────────
/**
 * Per-NFT gas deposit: covers item storage + compute overhead.
 * Based on real mainnet data: collection compute ~0.013-0.019 TON,
 * NFT storage ~0.012 TON/yr.
 */
export const MINT_GAS_PER_NFT = toNano('0.015');
/** Base collection compute margin for PublicMint operations. */
export const MINT_GAS_BASE = toNano('0.05');
/**
 * Base collection compute margin for MinterMint / MinterMintWithContent.
 * Lower than `MINT_GAS_BASE` because the minter path skips the whitelist,
 * blacklist, mint-phase, and per-wallet checks that PublicMint runs. Keep
 * in sync with what `nft_collection.tact` and `async_minter_collection.tact`
 * actually need for their minter receivers.
 */
export const MINTER_MINT_GAS_BASE = toNano('0.025');
/**
 * Safety margin added to `MINT_GAS_BASE` on every PublicMint preflight.
 * Covers gas-price volatility and any future contract changes that might
 * raise the required minimum by a few milliton. #340 — prevents partial
 * mint batches from burning `next_item_index` slots.
 */
export const MINT_GAS_SAFETY_MARGIN = toNano('0.02');
/** Gas for user reveal (NFT -> Collection -> NFT round-trip). Bumped from 0.05 to reduce bounce risk. */
export const USER_REVEAL_GAS = toNano('0.1');
/** Gas attached to EnableReveal admin message. */
export const ENABLE_REVEAL_VALUE = toNano('0.05');
/** Gas for owner withdraw operation. */
export const WITHDRAW_GAS = toNano('0.02');
/**
 * Minimum TON balance the collection contract must retain post-withdraw.
 * Enforced on-chain by `require(remaining >= ton("1"), "MIN_BALANCE")` in
 * both `nft_collection.tact` and `async_minter_collection.tact`. Any
 * off-chain tooling that computes a withdraw amount must subtract this
 * value from the contract's balance before deriving `amount`.
 */
export const MIN_COLLECTION_STORAGE = toNano('1');
/**
 * v3 (Tolk) collections enforce a much lower storage floor than v1: the
 * Withdraw receiver reserves `MIN_TONS_FOR_STORAGE = ton("0.05")` (see
 * `v3-pilot/contracts/fees-management.tolk` + the Withdraw handler in
 * `NftCollection.tolk`, which requires
 * `originalBalance - amount >= MIN_TONS_FOR_STORAGE`). Using the v1 floor
 * (1 TON) for a v3 collection over-reserves; using a flat 0.1 TON for a v1
 * collection UNDER-reserves and the withdraw bounces on `MIN_BALANCE`.
 */
export const MIN_COLLECTION_STORAGE_V3 = toNano('0.05');
/** Gas for registry operations (register, verify, unverify, withdraw fees). */
export const REGISTRY_GAS = toNano('0.05');
// ── Admin / deployer operations ────────────────────────────────────
/** Gas for deploying a collection contract (stateInit message). */
export const DEPLOY_VALUE = toNano('0.1');
/** Gas for configuration messages (ConfigureContent, ConfigureMint, etc.). */
export const CONFIGURE_VALUE = toNano('0.05');
/** Default gas for admin operations (whitelist add/remove, etc.). */
export const ADMIN_GAS = toNano('0.05');
/**
 * Per-NFT gas for the owner's OwnerMint path (free admin mint without
 * per-wallet tracking). The collection forwards exactly `ton("0.015")`
 * per NftItem deploy (see mintNft in nft_collection.tact); ~0.003 TON of
 * cell-forwarding fee keeps the receiving wallet solvent. #325: previous
 * value of 0.04 TON stranded ~0.022 TON per item in the collection
 * contract on every OwnerMint batch.
 */
export const OWNER_MINT_GAS_PER_NFT = toNano('0.018');
/** Collection-level compute overhead for the OwnerMint receiver. */
export const OWNER_MINT_GAS_BASE = toNano('0.01');
/**
 * v3 (Tolk) OwnerMint funding. UNLIKE v1, the v3 collection funds each NFT
 * item deploy *only* from the incoming message value and refuses to dip
 * below its storage floor — so OwnerMint must be self-sufficient, exactly
 * like v3 PublicMint. These mirror the contract-side literals in
 * `v3-pilot/contracts/NftCollection.tolk` (PublicMint `minRequired`):
 *   per-item deploy `ton("0.03")` + per-item overhead `ton("0.005")`
 *   + flat base `ton("0.05")`.
 * The v1 constants above (0.018/item + 0.01 base) under-fund a v3 mint:
 * compute passes every assert, then the action phase can't fund the
 * item-deploy sends and the tx aborts with no compute exit code. Keep
 * these in lock-step with the OwnerMint receiver in NftCollection.tolk.
 */
export const OWNER_MINT_GAS_PER_NFT_V3 = toNano('0.035');
/** v3 collection-level base for OwnerMint — matches v3 PublicMint's flat base. */
export const OWNER_MINT_GAS_BASE_V3 = toNano('0.05');
/**
 * Per-NFT gas deposit for v3 MinterMint (relayer/server path).
 * V3 Tolk deploys each item for ton("0.03") (same as OwnerMint/PublicMint).
 * The v1 constant (MINT_GAS_PER_NFT = 0.015) under-funds v3 minter mints:
 * compute passes every assert, then the action phase can't fund item deploys
 * and the tx aborts with exitCode=unknown. Keep in lock-step with the
 * MinterMint receiver in v3-pilot/contracts/NftCollection.tolk.
 */
export const MINTER_MINT_GAS_PER_NFT_V3 = toNano('0.035');
/** v3 collection-level base for MinterMint — matches OwnerMint's flat base. */
export const MINTER_MINT_GAS_BASE_V3 = toNano('0.05');
// ── Custodial / indexer operations ─────────────────────────────────
/** Gas for custodial NFT Transfer message (claim worker). */
export const TRANSFER_GAS = toNano('0.05');
/**
 * Minimum balance the custodial claim worker keeps on the source wallet
 * before attempting another transfer. Matches the min-reserve the worker
 * applies before send.
 */
export const CLAIM_MIN_RESERVE = toNano('0.1');
// ── Preflight helpers ──────────────────────────────────────────────
//
// These helpers give frontends and the admin API a single place to ask
// "how much TON does this transaction need?". They are used to implement
// wallet-balance checks that reject under-funded mints BEFORE the tx is
// sent, so a partial mint can never burn `next_item_index` slots. See
// #340 for the failure mode being prevented.
/**
 * Required value for a PublicMint message with `quantity` items at
 * `pricePerNft` each. Returns both the minimum value the contract will
 * accept (tight) and a recommended value that includes a safety margin
 * for gas volatility.
 *
 * The minimum matches the contract-side check:
 *   `msg_value >= price*qty + MINT_GAS_PER_NFT*qty + MINT_GAS_BASE`
 * (see `nft_collection.tact` PublicMint receiver).
 *
 * Frontends should always send `recommended`, never `min`, because
 * miner fees are not perfectly predictable at submission time.
 */
export function estimatePublicMintValue(quantity, pricePerNft) {
    const q = typeof quantity === 'number' ? BigInt(quantity) : quantity;
    if (q <= 0n) {
        throw new Error(`estimatePublicMintValue: quantity must be > 0, got ${q}`);
    }
    const price = pricePerNft * q;
    const gas = MINT_GAS_PER_NFT * q + MINT_GAS_BASE;
    const min = price + gas;
    const recommended = min + MINT_GAS_SAFETY_MARGIN;
    return { min, recommended };
}
/**
 * Required value for an OwnerMint message with `quantity` items.
 * Returns a single conservative value — no safety-margin variant
 * because OwnerMint is only called by trusted admin tooling that can
 * retry cleanly.
 *
 * Version-aware: v1 (Tact) collections retain balance and subsidize the
 * cheaper 0.015/item deploy, so the historical 0.018/item + 0.01 base is
 * fine. v3 (Tolk) collections deploy thin and fund item deploys only from
 * the incoming value (mirroring v3 PublicMint), so they need the larger
 * 0.035/item + 0.05 base or the action phase aborts. Default stays 'v1'
 * to preserve existing callers.
 */
export function estimateOwnerMintValue(quantity, contractVersion = 'v1') {
    const q = typeof quantity === 'number' ? BigInt(quantity) : quantity;
    if (q <= 0n) {
        throw new Error(`estimateOwnerMintValue: quantity must be > 0, got ${q}`);
    }
    if (contractVersion === 'v3') {
        return OWNER_MINT_GAS_PER_NFT_V3 * q + OWNER_MINT_GAS_BASE_V3;
    }
    return OWNER_MINT_GAS_PER_NFT * q + OWNER_MINT_GAS_BASE;
}
/**
 * Required value for a MinterMint or MinterMintWithContent message,
 * the trusted-relayer mint path used by the async-mint pipeline. Uses
 * the lower `MINTER_MINT_GAS_BASE` because the minter path skips the
 * whitelist / blacklist / phase / per-wallet checks that PublicMint
 * runs. There is no price component — the minter does not pay on-chain.
 *
 * Pass `contractVersion: 'v3'` for Tolk collections — they deploy each
 * NFT item for ton("0.03") vs v1's ton("0.015"), so the v1 formula
 * under-funds the action phase and causes silent exitCode=unknown aborts.
 * Defaults to 'v1' to preserve all existing callers.
 */
export function estimateMinterMintValue(quantity, contractVersion = 'v1') {
    const q = typeof quantity === 'number' ? BigInt(quantity) : quantity;
    if (q <= 0n) {
        throw new Error(`estimateMinterMintValue: quantity must be > 0, got ${q}`);
    }
    if (contractVersion === 'v3') {
        return MINTER_MINT_GAS_PER_NFT_V3 * q + MINTER_MINT_GAS_BASE_V3;
    }
    return MINT_GAS_PER_NFT * q + MINTER_MINT_GAS_BASE;
}
/**
 * Preflight check: does the wallet have enough balance for a PublicMint
 * of the given quantity at the given price? Uses the `recommended`
 * value (includes safety margin) to avoid false negatives from gas
 * volatility.
 *
 * Frontends should call this before submitting a mint transaction. If
 * it returns false, show a "insufficient balance" message rather than
 * letting the transaction be sent and bounced mid-batch.
 */
export function hasEnoughForPublicMint(walletBalance, quantity, pricePerNft) {
    const { recommended } = estimatePublicMintValue(quantity, pricePerNft);
    return walletBalance >= recommended;
}
/**
 * Storage floor that must remain on a collection contract after a withdraw,
 * keyed by contract version. v1 enforces 1 TON on-chain; v3 enforces 0.05 TON.
 */
export function collectionStorageFloor(contractVersion = 'v1') {
    return contractVersion === 'v3' ? MIN_COLLECTION_STORAGE_V3 : MIN_COLLECTION_STORAGE;
}
/**
 * Maximum amount that can safely be withdrawn from the collection's current
 * balance while keeping the version-appropriate storage floor. Returns 0n if
 * `currentBalance <= floor`. Defaults to v1 for backward compatibility.
 */
export function maxSafeWithdraw(currentBalance, contractVersion = 'v1') {
    const floor = collectionStorageFloor(contractVersion);
    if (currentBalance <= floor)
        return 0n;
    return currentBalance - floor;
}
